/**
 * Browser-side Supabase client.
 *
 * Uses the anon key from `src/lib/env.ts`. The anon key is safe to ship in
 * the bundle — RLS policies on the database (see `src/data/schema.sql`)
 * are the actual gate.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { env } from "./env";

/**
 * No-op cross-tab lock — runs the function immediately, never blocks.
 *
 * Why: supabase-js defaults to a `navigator.locks`-based mutex so two tabs
 * don't simultaneously refresh the auth token. The implementation has a
 * known hang issue where one tab's lock can stick (slow query, GC pause,
 * devtools open) and **every other tab waits forever** for it to release.
 * Symptom: open a second tab on the site and it sits at the page header
 * with no data, queries never resolve, closing the locking tab unsticks it.
 *
 * Trade-off: two tabs MAY race on token refresh, but supabase-js's HTTP
 * layer already retries 401s by refreshing, so the worst case is one
 * extra refresh round-trip. Better than tabs hanging indefinitely.
 */
const noopLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => fn();

export const supabase: SupabaseClient = createClient(
  env.SUPABASE_URL,
  env.SUPABASE_ANON_KEY,
  {
    auth: {
      // Persist sessions in localStorage so an operator stays logged in across
      // refreshes.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // PKCE for the flows that round-trip through a URL (Google OAuth,
      // password-recovery links): the auth code in the URL is useless without
      // the code verifier this browser holds. Password sign-in is direct and
      // unaffected. Note the built-in constraint: a recovery/OAuth link must
      // be opened in the same browser that started the flow.
      flowType: "pkce",
      lock: noopLock,
    },
  },
);
